// src/padplanner/planLayout.js
//
// One revision's layout on a map: the FootprintEditor that draws its features
// and the ChecksLayers that draw its witness lines and redzone halos. The
// editor (EditorPage.jsx) and the plan page (PlanPage.jsx, S6a2) both mount
// it through here, the plan page with readOnly, so the two cannot draw the
// same revision differently: the same instance id, the same layers, in the
// same order, from the same server geometry. PlanLayout.parity.test.jsx
// holds them to that.
import FootprintEditor from "../lib/footprint-editor/FootprintEditor.js";
import ChecksLayers from "./ChecksLayers";
import { redzones } from "./clearanceMirror";
import { boundsOf, USER_ACTION } from "./planMapData";
import { LINE_COLORS, lineTypeLabel } from "./lineVocabulary";

// Both pages use it, so a map never holds two.
export const LAYOUT_INSTANCE = "pp";

/**
 * Mount the layout. `editor` takes FootprintEditor's options (map, features,
 * localEpsg, perimeter, templates, readOnly, the callbacks); the instance id
 * is always LAYOUT_INSTANCE.
 */
export function mountLayout(options) {
  const editor = new FootprintEditor({ lineColors: LINE_COLORS, ...options, instanceId: LAYOUT_INSTANCE });
  const checks = new ChecksLayers({ map: options.map, below: editor.id("fill") });
  return {
    editor,
    checks,
    destroy() { checks.destroy(); editor.destroy(); },
  };
}

/** What a feature is called on the page: its template's name, a line's type, or its kind. */
export function featureName(f, templatesById) {
  const t = f?.template_id ? templatesById.get(f.template_id) ?? null : null;
  return t?.name ?? (f?.kind === "line" ? lineTypeLabel(f.layer) : f?.kind === "zone" ? "Zone" : "Footprint");
}

/** The halos for what the editor draws now, against `rules`. Call after each change. */
export function drawRedzones(layout, { rules, templatesById }) {
  if (!layout) return;
  const { editor, checks } = layout;
  const templateOf = (f) => (f.template_id ? templatesById.get(f.template_id) ?? null : null);
  checks.setRedzones(redzones({ features: editor.getFeatures(), templateOf, frame: editor.frame, rules }));
}

/**
 * Whose rules the halos follow: a submitted revision's, those it was checked
 * against (its pinned run's set); a draft's, the org's published set now, as
 * the editor's live reading does.
 */
export function layoutRuleSetId({ frozen, run, publishedId }) {
  return frozen ? run?.rule_set_id ?? null : publishedId ?? null;
}

/**
 * A Checks row picked: select the features it names and frame them with the
 * witness. The user asked, so the camera move is passed as USER_ACTION
 * (THE CAMERA in PadPlannerMap.jsx).
 */
export function frameResult(layout, map, r) {
  const ed = layout?.editor;
  if (!ed || !map) return;
  const ids = [r.subject?.feature_id, r.object?.feature_id].filter((id) => id && ed.getFeature(id));
  ed.select(ids);
  const boxes = [...ids.map((id) => boundsOf(ed.displayGeometry(ed.getFeature(id)))), boundsOf(r.witness)].filter(Boolean);
  if (!boxes.length) return;
  const b = boxes.reduce((a, x) => [Math.min(a[0], x[0]), Math.min(a[1], x[1]), Math.max(a[2], x[2]), Math.max(a[3], x[3])]);
  map.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 90, maxZoom: 20, duration: 300 }, USER_ACTION);
}
